import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Quote, Zap, Users, Target, Star } from "lucide-react";
import { Helmet } from "react-helmet-async";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function Home() {
  return (
    <>
      <Helmet>
        <title>Ta' JA-Hatten På! v/ Candra Gaarde – Foredrag & Selvindsigt med humor</title>
        <meta name="description" content="Oplev motiverende foredrag og oplæg af Candra Gaarde. Bryd tavsheden om tabuer, fordomme, senfølger og psykiatri med humor, nærvær og dyb indsigt." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-24 pb-6 lg:pt-32 lg:pb-8 overflow-hidden bg-[#fafafa]">
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-3 px-3 py-1 border-b border-red-600 text-red-600 text-sm font-semibold tracking-[0.2em] uppercase mb-12"
            >
              <div className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse [animation-delay:0.2s]" />
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse [animation-delay:0.4s]" />
              </div>
              <span>Eksklusiv Motivation & Indsigt</span>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="flex justify-center mb-12"
            >
              <img 
                src="https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/5c2b55a0-9c8e-48b9-a51c-b44d556f6237.png" 
                alt="Ta Ja-Hatten På!" 
                className="h-24 md:h-36 lg:h-44 w-auto object-contain mx-auto"
              />
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg md:text-2xl text-slate-600 mb-6 leading-relaxed max-w-2xl mx-auto font-light"
            >
              Ta´ JA-Hatten På! Og vær med til at bryde tavsheden om fordomme og tabuer! Vær nysgerrig, og få en ny bredere viden og forståelse bag om mennesket.
              <span className="block text-red-600 font-bold mt-6">
                Positive tanker & idéer smitter!
              </span>
            </motion.p>
          </div>
        </div>
      </section>

      {/* Image Showcase */}
      <section className="pb-20 bg-[#fafafa]">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="relative aspect-[21/9] w-full overflow-hidden grayscale hover:grayscale-0 transition-all duration-1000 group"
          >
            <img
              src="https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/f65d4098-54d7-44c2-a74c-4bff8d40c406.jpg"
              alt="Candra Gaarde på scenen"
              className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-1000"
            />
            <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-1000" />
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col sm:flex-row justify-center gap-8 mt-12"
          >
            <Link
              to="/spoerg-ikke"
              className="group relative inline-flex items-center justify-center px-6 py-3.5 md:px-10 md:py-5 bg-red-600 text-white font-semibold tracking-wide transition-all hover:bg-red-700"
            >
              <span>mine faglige erfaringer</span>
              <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/foredrag"
              className="group relative inline-flex items-center justify-center px-6 py-3.5 md:px-10 md:py-5 border border-slate-900 text-slate-900 font-semibold tracking-wide transition-all hover:bg-slate-900 hover:text-white"
            >
              <span>emner jeg holder foredrag i</span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-end mb-16 lg:mb-32">
            <div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-normal text-slate-900 mb-8 tracking-tight">
                Hvordan vælger man at <br />
                <span className="italic font-serif text-red-600">Ta´ JA-Hatten På?</span>
              </h2>
              <div className="flex gap-1 mt-6">
                <span className="w-10 h-1 bg-red-600" />
                <span className="w-10 h-1 bg-emerald-500" />
                <span className="w-10 h-1 bg-amber-400" />
              </div>
            </div>
            <div>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed font-light max-w-lg">
                Jeg tror på, at ved at give det videre, og dele ud af personlig erfaring, kan det være med til at hjælpe dem som har det svært. Sammen er vi alle med til at gøre en forskel.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-px bg-slate-100 border border-slate-100">
            {[
              {
                title: "Skræddersyede Foredrag",
                desc: "Alle foredrag bliver tilrettelagt ud fra netop jeres ønsker. Der er derfor ingen foredrag som er ens.",
                icon: <Users size={24} strokeWidth={1.5} />,
                colorClass: "text-red-600",
                hoverColorClass: "group-hover:text-red-600",
                borderClass: "hover:border-red-600"
              },
              {
                title: "Erfaringsbaseret Viden",
                desc: "Jeg bruger min erfaringsbaseret viden og forskellige teknikker, hvor man som deltager får idéer til at tænke ud af boksen, sætter grænser, hvor man også åbner op for kommunikation.",
                icon: <Target size={24} strokeWidth={1.5} />,
                colorClass: "text-emerald-600",
                hoverColorClass: "group-hover:text-emerald-600",
                borderClass: "hover:border-emerald-500"
              },
              {
                title: "TABU Emner",
                desc: "Jeg tager bla. fat i TABU emnerne, inden for: Psykiskesygdomme, seksuelle overgreb, alkoholmisbrug og meget mere.",
                icon: <Zap size={24} strokeWidth={1.5} />,
                colorClass: "text-amber-500",
                hoverColorClass: "group-hover:text-amber-500",
                borderClass: "hover:border-amber-400"
              }
            ].map((item, i) => (
              <div 
                key={i} 
                className={`bg-white p-8 md:p-16 group hover:bg-slate-50 transition-all border-t-2 border-transparent ${item.borderClass} ${
                  i === 2 ? "md:col-span-2 xl:col-span-1" : ""
                }`}
              >
                <div className={`mb-10 text-slate-400 ${item.hoverColorClass} transition-colors ${item.colorClass}`}>
                  {item.icon}
                </div>
                <h3 className={`text-2xl font-serif italic mb-6 text-slate-900 ${item.hoverColorClass} transition-colors`}>{item.title}</h3>
                <div className="text-slate-600 leading-relaxed font-light">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Concept - Spørg Ikke */}
      <section className="section-padding bg-slate-900 text-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-24 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-[4/5] md:aspect-auto md:h-[500px] overflow-hidden grayscale">
                <img
                  src="https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/220a1f57-dbd6-4a2b-bef0-e07a1ca05fa2.jpg"
                  alt="Spørg Ikke Koncept"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-12 -right-12 w-2/3 aspect-square overflow-hidden border-[12px] border-slate-900 hidden lg:block">
                <img
                  src="https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/753daee2-1af1-43d9-bc62-d1cf3074eaed.jpg"
                  alt="Detalje"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-red-500 font-semibold tracking-[0.3em] uppercase text-sm mb-8 block">Signatur Koncept</span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-normal mb-10 tracking-tight leading-tight">
                Spørg ikke:<br />
                <span className="italic font-serif text-red-500 text-3xl md:text-4xl lg:text-5xl">"Hvad er der galt med dig?"</span><br />
                Men spørg:<br />
                <span className="italic font-serif text-red-500 text-3xl md:text-4xl lg:text-5xl">"Hvad er der sket med dig?"</span>
              </h2>
              <p className="text-lg md:text-xl text-slate-400 mb-12 leading-relaxed font-light">
                Et inspirerende oplæg, der giver stof til refleksion for både personale, ledere, studerende, pårørende og alle andre, inklusiv patienter!
              </p>
              <Link
                to="/spoerg-ikke"
                className="inline-flex items-center gap-4 text-white border-b border-white/20 pb-2 hover:border-red-500 transition-all group"
              >
                <span className="text-lg font-semibold tracking-wide">Udforsk konceptet</span>
                <ArrowRight className="group-hover:translate-x-2 transition-transform text-red-500" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Quote size={48} className="mx-auto text-red-600/20 mb-12" />
            <h2 className="text-xl md:text-4xl font-serif italic text-slate-900 leading-tight mb-8">
              "Candra formår at ramme en nerve, som få andre kan. Det er ikke bare et foredrag, det er en transformation af måden I arbejder sammen på."
            </h2>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 mb-4 overflow-hidden grayscale">
                <img src="https://i.pravatar.cc/150?u=hr" alt="HR Chef" />
              </div>
              <p className="font-semibold text-slate-900 tracking-wide">Mette Frederiksen</p>
              <p className="text-sm text-slate-500 uppercase tracking-widest">HR Direktør, Global Tech</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-20 bg-[#fafafa] border-t border-slate-100">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex justify-center gap-1.5 mb-8">
              <span className="w-3 h-3 rounded-full bg-red-600" />
              <span className="w-3 h-3 rounded-full bg-emerald-500" />
              <span className="w-3 h-3 rounded-full bg-amber-400" />
            </div>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-normal text-slate-900 mb-12 tracking-tight">
              Sammen skaber vi <br />
              <span className="italic font-serif text-red-600">forandring</span>
            </h2>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-12">
              <Link
                to="/kontakt"
                className="group text-2xl font-serif italic text-slate-900 hover:text-red-600 transition-colors flex items-center gap-4"
              >
                Send et forespørgsel
                <ArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
